import styled from 'styled-components';

const StyledRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const StyledSectionLabel = styled.h4`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 0 0 0.5rem;
`;

const StyledRequestTextarea = styled.textarea<{ $hasError: boolean }>`
  width: 100%;
  box-sizing: border-box;
  font-family: monospace;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  resize: vertical;
  border: ${({ $hasError, theme }) =>
    $hasError ? `1.5px solid ${theme.colors.dangerText}` : `1px solid ${theme.colors.borderMuted}`};
  border-radius: ${({ theme }) => theme.radii.sm};
  padding: 0.5rem;
  background: ${({ theme }) => theme.colors.surfaceMuted};
  line-height: 1.5;
`;

const StyledFileRow = styled.div<{ $variant: 'selected' | 'previous' | 'empty' }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.5rem;
  border-radius: ${({ theme }) => theme.radii.sm};

  ${({ $variant, theme }) => {
    if ($variant === 'selected') {
      return `border: 1px solid ${theme.colors.borderMuted}; background: ${theme.colors.surfaceMuted};`;
    }

    if ($variant === 'previous') {
      return `border: 1px dashed ${theme.colors.borderMuted}; background: ${theme.colors.surfaceWarning}; color: ${theme.colors.textAccentWarn};`;
    }

    return `border: 1px dashed ${theme.colors.borderDashed}; background: ${theme.colors.surfaceMuted}; cursor: pointer; font-size: ${theme.fontSizes.sm}; color: ${theme.colors.textSecondary};`;
  }};
`;

const StyledFileName = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledFileSize = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`;

const StyledReselectLabel = styled.label`
  cursor: pointer;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.primary};
`;

const StyledHiddenFileInput = styled.input`
  display: none;
`;

const StyledRunRow = styled.div`
  display: flex;
  gap: 6px;
`;

const StyledRunButton = styled.button`
  flex: 1;
`;

const StyledResetButton = styled.button`
  flex-shrink: 0;
`;

const StyledJobCard = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.borderMuted};
  border-radius: ${({ theme }) => theme.radii.md};
  padding: 0.625rem 0.75rem;
  background: ${({ theme }) => theme.colors.surfaceMuted};
`;

const StyledJobHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.375rem;
`;

const StyledJobTitle = styled.span`
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const StyledJobStatus = styled.span<{ $color: string }>`
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const StyledStatusPulse = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.statusRunning};
  animation: pulse 1s infinite;
`;

const StyledJobId = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: monospace;
  margin-bottom: 0.375rem;
  word-break: break-all;
`;

const StyledStatsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const StyledCompletedCount = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.statusSucceeded};
`;

const StyledSkippedCount = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.statusSkipped};
`;

const StyledErrorTargetTag = styled.span`
  margin-left: 0.375rem;
  opacity: 0.75;
`;

const StyledErrorTargetCode = styled.code`
  background: ${({ theme }) => theme.colors.errorTargetSurface};
  padding: 0 2px;
  border-radius: 2px;
`;

const StyledOutputBlock = styled.div`
  margin-top: 0.5rem;
`;

const StyledOutputLabel = styled.div`
  font-weight: 600;
  font-size: 11px;
  margin-bottom: 0.25rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StyledOutputPre = styled.pre`
  margin: 0;
  padding: 0.375rem 0.5rem;
  background: ${({ theme }) => theme.colors.outputSurface};
  border-radius: ${({ theme }) => theme.radii.sm};
  font-size: 11px;
  font-family: monospace;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
`;

const StyledClearHighlightsButton = styled.button`
  margin-top: 0.5rem;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

/** Tiny ✕ button used to clear/remove a selected file inside the file row. */
const StyledCloseIconButton = styled.button`
  padding: 0 0.25rem;
  font-size: ${({ theme }) => theme.fontSizes.xs};
`;

/** Wrapper that contributes only the small top-margin spacing around the
 *  shared <ErrorMessage> when it appears below the request textarea. */
const StyledRequestErrorSlot = styled.div`
  margin-top: 0.25rem;
`;

/** Wrapper that contributes only the slightly larger top-margin around the
 *  shared <ErrorMessage> when it appears inside the job-status card. */
const StyledJobErrorSlot = styled.div`
  margin-top: 0.375rem;
`;

export default {
  StyledRoot,
  StyledSectionLabel,
  StyledRequestTextarea,
  StyledFileRow,
  StyledFileName,
  StyledFileSize,
  StyledReselectLabel,
  StyledHiddenFileInput,
  StyledRunRow,
  StyledRunButton,
  StyledResetButton,
  StyledJobCard,
  StyledJobHeader,
  StyledJobTitle,
  StyledJobStatus,
  StyledStatusPulse,
  StyledJobId,
  StyledStatsRow,
  StyledCompletedCount,
  StyledSkippedCount,
  StyledErrorTargetTag,
  StyledErrorTargetCode,
  StyledOutputBlock,
  StyledOutputLabel,
  StyledOutputPre,
  StyledClearHighlightsButton,
  StyledCloseIconButton,
  StyledRequestErrorSlot,
  StyledJobErrorSlot,
};
